import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSocket } from "hooks/useSocket";
import type { QuizDetail, QuizStatus } from "types/quiz";
import { quizQueryKeys } from "./quizQueryKeys";

const QUIZ_SOCKET_EVENTS = {
  join: "quiz:join",
  leave: "quiz:leave",
  playerJoined: "quiz:player_joined",
  playerLeft: "quiz:player_left",
  playerPart1Complete: "quiz:player_part1_complete",
  playerPart2Complete: "quiz:player_part2_complete",
  statusChange: "quiz:status_change",
} as const;

const QUIZ_STATUS_SET = new Set<QuizStatus>(["lobby", "part1", "part2", "ended"]);

interface UseQuizSocketOptions {
  code: string | null;
  enabled: boolean;
}

/**
 * Subscribe the current socket connection to one quiz room and invalidate the
 * relevant query cache whenever realtime events arrive.
 */
export function useQuizSocket({ code, enabled }: UseQuizSocketOptions): { isRealtimeConnected: boolean } {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    const normalizedCode = String(code || "").trim().toLowerCase();
    if (!enabled || !normalizedCode || typeof document === "undefined") {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      void Promise.all([
        queryClient.invalidateQueries({ queryKey: quizQueryKeys.detail(normalizedCode) }),
        queryClient.invalidateQueries({ queryKey: quizQueryKeys.results(normalizedCode) }),
      ]);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [code, enabled, queryClient]);

  useEffect(() => {
    const normalizedCode = String(code || "").trim().toLowerCase();
    if (!enabled || !normalizedCode || !socket) {
      return;
    }

    const invalidate = () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: quizQueryKeys.detail(normalizedCode) }),
        queryClient.invalidateQueries({ queryKey: quizQueryKeys.results(normalizedCode) }),
      ]);
    };

    const handleStatusChange = (payload?: { status?: string; startedAt?: string | null; endedAt?: string | null }) => {
      const nextStatus = payload?.status;
      queryClient.setQueryData<QuizDetail | undefined>(quizQueryKeys.detail(normalizedCode), (current) => {
        if (!current?.quiz) {
          return current;
        }
        return {
          ...current,
          quiz: {
            ...current.quiz,
            status: nextStatus && QUIZ_STATUS_SET.has(nextStatus as QuizStatus)
              ? (nextStatus as QuizStatus)
              : current.quiz.status,
            started_at: payload?.startedAt ?? current.quiz.started_at,
            ended_at: payload?.endedAt ?? current.quiz.ended_at,
          },
        };
      });
      invalidate();
    };

    const joinRoom = () => {
      socket.emit(QUIZ_SOCKET_EVENTS.join, { code: normalizedCode });
    };

    if (socket.connected) {
      joinRoom();
    }

    socket.on("connect", joinRoom);
    socket.on(QUIZ_SOCKET_EVENTS.playerJoined, invalidate);
    socket.on(QUIZ_SOCKET_EVENTS.playerLeft, invalidate);
    socket.on(QUIZ_SOCKET_EVENTS.playerPart1Complete, invalidate);
    socket.on(QUIZ_SOCKET_EVENTS.playerPart2Complete, invalidate);
    socket.on(QUIZ_SOCKET_EVENTS.statusChange, handleStatusChange);

    return () => {
      socket.emit(QUIZ_SOCKET_EVENTS.leave, { code: normalizedCode });
      socket.off("connect", joinRoom);
      socket.off(QUIZ_SOCKET_EVENTS.playerJoined, invalidate);
      socket.off(QUIZ_SOCKET_EVENTS.playerLeft, invalidate);
      socket.off(QUIZ_SOCKET_EVENTS.playerPart1Complete, invalidate);
      socket.off(QUIZ_SOCKET_EVENTS.playerPart2Complete, invalidate);
      socket.off(QUIZ_SOCKET_EVENTS.statusChange, handleStatusChange);
    };
  }, [code, enabled, queryClient, socket]);

  return {
    isRealtimeConnected: Boolean(enabled && socket && isConnected),
  };
}
